// Pixel maps → one atlas texture (docs/08 §7: a single atlas generated at boot).
// Blur frames are a palette-preserving vertical smear (no blended colors).
import { Rectangle, Texture, type TextureSource } from 'pixi.js';
import { PALETTE, type PaletteKey } from './palette';
import { glyph, GLYPH_H, GLYPH_W, textWidth } from './pixelFont';
import type { SpriteDef } from './sprites/types';

type Pixels = (number | null)[][]; // rgb or null, [y][x]

function toPixels(def: SpriteDef, frame: number): Pixels {
  const rows = def.frames[frame] ?? def.frames[0]!;
  return rows.map((row) =>
    [...row].map((ch) => {
      if (ch === '.' || ch === ' ') return null;
      const key = def.colors[ch];
      return key ? PALETTE[key] : null;
    }),
  );
}

/** Trailing vertical streak; level 2 also drops every other row for a motion-line feel. */
export function smear(px: Pixels, level: 1 | 2): Pixels {
  const h = px.length;
  const w = px[0]?.length ?? 0;
  const out: Pixels = [];
  for (let y = 0; y < h; y++) {
    const row: (number | null)[] = [];
    for (let x = 0; x < w; x++) {
      let c = px[y]![x] ?? null;
      for (let k = 1; c === null && k <= level * 3; k++) c = y - k >= 0 ? (px[y - k]![x] ?? null) : null;
      if (level === 2 && y % 2 === 1) c = y - 1 >= 0 ? (px[y - 1]![x] ?? c) : c;
      row.push(c);
    }
    out.push(row);
  }
  return out;
}

interface Placed {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class Atlas {
  private frames = new Map<string, Texture>();
  private source: TextureSource | null = null;

  constructor(sprites: SpriteDef[]) {
    const items: { key: string; px: Pixels }[] = [];
    for (const s of sprites) {
      s.frames.forEach((_, f) => items.push({ key: `${s.id}#${f}`, px: toPixels(s, f) }));
      if (s.id.startsWith('sym_')) {
        const base = toPixels(s, 0);
        items.push({ key: `${s.id}#blur1`, px: smear(base, 1) });
        items.push({ key: `${s.id}#blur2`, px: smear(base, 2) });
      }
    }
    // Shelf packing, tallest first.
    items.sort((a, b) => b.px.length - a.px.length);
    const W = 1024;
    const placed = new Map<string, Placed>();
    let x = 1,
      y = 1,
      shelf = 0;
    for (const it of items) {
      const w = it.px[0]?.length ?? 0;
      const h = it.px.length;
      if (x + w + 1 > W) {
        x = 1;
        y += shelf + 1;
        shelf = 0;
      }
      placed.set(it.key, { x, y, w, h });
      x += w + 1;
      shelf = Math.max(shelf, h);
    }
    const H = y + shelf + 1;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    const img = ctx.createImageData(W, H);
    for (const it of items) {
      const p = placed.get(it.key)!;
      it.px.forEach((row, py) =>
        row.forEach((c, px) => {
          if (c === null) return;
          const i = ((p.y + py) * W + p.x + px) * 4;
          img.data[i] = (c >> 16) & 255;
          img.data[i + 1] = (c >> 8) & 255;
          img.data[i + 2] = c & 255;
          img.data[i + 3] = 255;
        }),
      );
    }
    ctx.putImageData(img, 0, 0);
    const baseTex = Texture.from(canvas);
    baseTex.source.scaleMode = 'nearest';
    this.source = baseTex.source;
    for (const [key, p] of placed)
      this.frames.set(key, new Texture({ source: this.source, frame: new Rectangle(p.x, p.y, p.w, p.h) }));
  }

  tex(id: string, frame = 0): Texture {
    return this.frames.get(`${id}#${frame}`) ?? this.frames.get(`${id}#0`) ?? Texture.EMPTY;
  }

  blur(id: string, level: 1 | 2): Texture {
    return this.frames.get(`${id}#blur${level}`) ?? this.tex(id);
  }

  has(id: string): boolean {
    return this.frames.has(`${id}#0`);
  }

  frameCount(id: string): number {
    let n = 0;
    while (this.frames.has(`${id}#${n}`)) n++;
    return n;
  }
}

let atlas: Atlas | null = null;
export function initAtlas(sprites: SpriteDef[]): Atlas {
  atlas = new Atlas(sprites);
  return atlas;
}
export function getAtlas(): Atlas {
  if (!atlas) throw new Error('atlas not initialized');
  return atlas;
}

// ── Pixel-font text textures ──────────────────────────────────────────

const textCache = new Map<string, Texture>();

/** Text in the 5×7 font, one palette color, optional 1-px outline. Cached. */
export function textTexture(text: string, color: PaletteKey, outline: PaletteKey | null = 'ink', scale = 1): Texture {
  const key = `${text}|${color}|${outline}|${scale}`;
  const hit = textCache.get(key);
  if (hit) return hit;
  const pad = outline ? 1 : 0;
  const w = textWidth(text) + pad * 2;
  const h = GLYPH_H + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, w * scale);
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d')!;
  const lit: [number, number][] = [];
  [...text].forEach((ch, i) => {
    const g = glyph(ch);
    for (let gy = 0; gy < GLYPH_H; gy++)
      for (let gx = 0; gx < GLYPH_W; gx++) if (g[gy]![gx] === '#') lit.push([pad + i * (GLYPH_W + 1) + gx, pad + gy]);
  });
  const fill = (c: PaletteKey, pts: [number, number][]) => {
    ctx.fillStyle = '#' + PALETTE[c].toString(16).padStart(6, '0');
    for (const [x, y] of pts) ctx.fillRect(x * scale, y * scale, scale, scale);
  };
  if (outline) {
    const ring: [number, number][] = [];
    for (const [x, y] of lit)
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (dx || dy) ring.push([x + dx, y + dy]);
    fill(outline, ring);
  }
  fill(color, lit);
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  if (textCache.size > 400) textCache.clear();
  textCache.set(key, tex);
  return tex;
}

/** A 2×2 checkerboard of ink used to dim cells without blending colors. */
let ditherTex: Texture | null = null;
export function ditherTexture(): Texture {
  if (ditherTex) return ditherTex;
  const c = document.createElement('canvas');
  c.width = 2;
  c.height = 2;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#' + PALETTE.ink.toString(16).padStart(6, '0');
  ctx.fillRect(0, 0, 1, 1);
  ctx.fillRect(1, 1, 1, 1);
  ditherTex = Texture.from(c);
  ditherTex.source.scaleMode = 'nearest';
  ditherTex.source.addressMode = 'repeat';
  return ditherTex;
}
